import { z } from 'zod';
import { MetaSchema } from './create-chat.js';
import { MemberRoleSchema } from './me.js';

/**
 * `GET /api/v1/workstreams/members` contract (ADR-0071 §6, D15-12) — the Users & Access
 * roster: Auth0's live organisation members joined to the roles we own, plus the invites
 * not yet accepted. Owner and Admin only. `data` is strict (the BFF authors it); the
 * envelope is loose (ADR-0024 §11). Names and emails are served here and nowhere else.
 */
export const MemberRowSchema = z.strictObject({
  userId: z.string(),
  name: z.string().optional(),
  email: z.string().optional(),
  role: MemberRoleSchema,
  /** ISO-8601 — the member's last Agentis visit, to the hour (D16-08). Absent = never seen since it shipped. */
  lastActiveAt: z.string().optional(),
});

export const PendingInviteRowSchema = z.strictObject({
  /** Opaque Auth0 invitation id — the withdraw key, so an email never rides in a URL. */
  invitationId: z.string(),
  email: z.string(),
  /** Invites never carry `owner` (ADR-0071 §6). */
  role: z.enum(['admin', 'member']),
  /** ISO-8601. */
  expiresAt: z.string(),
});

export const MembersDataSchema = z.strictObject({
  members: z.array(MemberRowSchema),
  pending: z.array(PendingInviteRowSchema),
});
export type MembersData = z.infer<typeof MembersDataSchema>;

export const MembersResponseSchema = z.looseObject({ data: MembersDataSchema, meta: MetaSchema });
export type MembersResponse = z.infer<typeof MembersResponseSchema>;

/**
 * Users & Access writes (ADR-0071 §6, D15-13). Invite: Owner or Admin. Role change and
 * remove: Owner only. `owner` is never grantable here — Owner grants are seed-script only.
 */
export const InviteRequestSchema = z.strictObject({
  email: z.email().max(254),
  role: z.enum(['admin', 'member']),
});
export type InviteRequest = z.infer<typeof InviteRequestSchema>;

export const InviteResponseSchema = z.looseObject({
  data: PendingInviteRowSchema,
  meta: MetaSchema,
});
export type InviteResponse = z.infer<typeof InviteResponseSchema>;

export const ChangeRoleRequestSchema = z.strictObject({ role: z.enum(['admin', 'member']) });
export type ChangeRoleRequest = z.infer<typeof ChangeRoleRequestSchema>;

export const MemberChangeDataSchema = z.strictObject({
  userId: z.string(),
  role: MemberRoleSchema,
});
export type MemberChangeData = z.infer<typeof MemberChangeDataSchema>;

export const MemberChangeResponseSchema = z.looseObject({
  data: MemberChangeDataSchema,
  meta: MetaSchema,
});
export type MemberChangeResponse = z.infer<typeof MemberChangeResponseSchema>;

export const InviteWithdrawnDataSchema = z.strictObject({ invitationId: z.string() });
export type InviteWithdrawnData = z.infer<typeof InviteWithdrawnDataSchema>;

export const InviteWithdrawnResponseSchema = z.looseObject({
  data: InviteWithdrawnDataSchema,
  meta: MetaSchema,
});

export const MemberRemovedDataSchema = z.strictObject({ userId: z.string() });
export type MemberRemovedData = z.infer<typeof MemberRemovedDataSchema>;

export const MemberRemovedResponseSchema = z.looseObject({
  data: MemberRemovedDataSchema,
  meta: MetaSchema,
});
export type MemberRemovedResponse = z.infer<typeof MemberRemovedResponseSchema>;
